import { compareDesc } from "date-fns";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Accept } from "react-dropzone";

import { RenderUploadedRow } from "@/components/DragAndDropFields/helper";
import { useAppRouteParams } from "../../../../AppRoutes";
import { postFileEvaluate, postFileOptimize } from "../../../../api/optimize";
import { useAppNotificationManager } from "../../../../components/AppProvider/AppNotificationProvider";
import { DialogPhaseStatesType } from "../../../Dialog/helpers/hooks";
import { useDialog } from "../../../Dialog/helpers/hooks";
import {
  DragAndDropFields,
  DragAndDropUploadData,
  dragAndDropAcceptFiles,
} from "../../../DragAndDropFields";
import { DragAndDropAcceptFileType } from "../../../DragAndDropFields";
import { useAlgorithmVersions } from "../../../InOrganizationProvider/AlgorithmVersionsProvider";
import styles from "./index.module.css";

import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { useVersionCategories } from "@/components/InOrganizationProvider/VersionCategoriesProvider";
import { translation } from "@/i18n";
import { nsUpload } from "@/i18n/locales/upload";
import { nsVersion } from "@/i18n/locales/version";
import { parseDateTimeOrISODateTime } from "@/utils/dateFnsHelper";
import {
  Checkbox,
  Dropdown,
  Field,
  Input,
  Option,
  OptionOnSelectData,
  SelectionEvents,
} from "@fluentui/react-components";

const accept: Accept = {
  ...dragAndDropAcceptFiles.excel,
  ...dragAndDropAcceptFiles.json,
  ...dragAndDropAcceptFiles.zip,
};

type FileOptimizeForm = Pick<
  Parameters<typeof postFileOptimize>[0],
  "comment" | "title" | "algorithm_version" | "category"
>;
const initalFileOptimizeForm: FileOptimizeForm = {
  comment: "",
  title: "",
  algorithm_version: "",
  category: "",
};

const useTranslation = translation.use(nsVersion, nsUpload);

export const FileOptimizeDialog: React.FC<DialogPhaseStatesType> = ({
  dialogPhase,
  setDialogPhase,
}) => {
  const { t } = useTranslation();
  const { organizationId } = useAppRouteParams();
  const { hasPermission } = usePermissions();
  const hasOptimizeWritePermission = hasPermission("optimize", "write");
  const hasEvaluateWritePermission = hasPermission("evaluate", "write");
  const { categoriesDropdown } = useVersionCategories();
  const displayedCategoriesDropdown = categoriesDropdown?.filter(
    (c) => c.value !== "all"
  );
  const { renderDialogs, showErrorAPIDialog } = useDialog({
    dialogPhase,
    setDialogPhase,
  });
  const { showSuccessNotification, showErrorNotification } =
    useAppNotificationManager();
  const isEvaluateInitialChecked =
    hasEvaluateWritePermission && !hasOptimizeWritePermission;
  const [isEvaluate, setIsEvaluate] = useState(isEvaluateInitialChecked);
  const { algorithmVersions } = useAlgorithmVersions();
  const algorithmVersionOptions: { value: string; text: string }[] = useMemo(
    () =>
      algorithmVersions
        ?.filter(({ is_disabled }) => !is_disabled)
        .sort((a, b) => {
          const _a = parseDateTimeOrISODateTime(a.created_at);
          const _b = parseDateTimeOrISODateTime(b.created_at);
          return _a && _b ? compareDesc(_a, _b) : 0;
        })
        .map(({ version_id, version_name }) => ({
          value: version_id,
          text: version_name,
        })) ?? [],
    [algorithmVersions]
  );

  const [existingType, setExistingType] = useState<
    DragAndDropAcceptFileType | undefined
  >();

  // アップされたデータ
  const [uploadedFileList, setUploadedFileList] = useState<
    DragAndDropUploadData[]
  >([]);

  const [fileOptimizeForm, setFileOptimizeForm] = useState<FileOptimizeForm>(
    initalFileOptimizeForm
  );

  // ダイアログを閉じたときフォームを初期化する
  useEffect(() => {
    if (!dialogPhase) {
      setFileOptimizeForm(initalFileOptimizeForm);
      setUploadedFileList([]);
      setIsEvaluate(isEvaluateInitialChecked);
    }
  }, [dialogPhase, isEvaluateInitialChecked]);

  const handleChangeTextField = useCallback(
    (
      event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldName: keyof FileOptimizeForm,
      value?: string | undefined
    ) => {
      setFileOptimizeForm((prevState) => ({
        ...prevState,
        [fieldName]: value ?? "",
      }));
    },
    []
  );

  const onChangeCategory = useCallback(
    (event: SelectionEvents, data: OptionOnSelectData) => {
      data.optionValue &&
        setFileOptimizeForm((prev) => ({
          ...prev,
          category: data.optionValue!,
        }));
    },
    []
  );

  const onChangeAlgorithmVersion = useCallback(
    (event: SelectionEvents, data: OptionOnSelectData) => {
      data.optionValue &&
        setFileOptimizeForm((prev) => ({
          ...prev,
          algorithm_version: data.optionValue!,
        }));
    },
    []
  );

  const runUpload = useCallback(async () => {
    setDialogPhase(null);
    showSuccessNotification(t.version.fileOptimize.startSuccess());
    try {
      const file = uploadedFileList[0].file;
      if (isEvaluate) {
        await postFileEvaluate({
          organizationId,
          file,
          ...fileOptimizeForm,
        });
      } else {
        await postFileOptimize({
          organizationId,
          file,
          ...fileOptimizeForm,
        });
      }
    } catch (error) {
      showErrorNotification(t.version.fileOptimize.error(), error);
    } finally {
      setFileOptimizeForm(initalFileOptimizeForm);
      setUploadedFileList([]);
      setIsEvaluate(false);
    }
  }, [t, uploadedFileList, fileOptimizeForm, isEvaluate]);

  const isDisableOptimize =
    !hasOptimizeWritePermission ||
    fileOptimizeForm.title === "" ||
    uploadedFileList.length === 0 ||
    fileOptimizeForm.algorithm_version === "";

  const isDisableEvaluate =
    !hasEvaluateWritePermission ||
    fileOptimizeForm.title === "" ||
    uploadedFileList.length === 0 ||
    fileOptimizeForm.algorithm_version === "";

  return (
    <>
      {renderDialogs({
        confirm: {
          title: t.version.fileOptimize.dialog.title(),
          buttonText: t.version.fileOptimize.dialog.button(),
          onClick: runUpload,
          onClickCancel: () => {
            setDialogPhase(null);
            setFileOptimizeForm(initalFileOptimizeForm);
            setUploadedFileList([]);
            setIsEvaluate(false);
          },
          isDisableButton: isEvaluate ? isDisableEvaluate : isDisableOptimize,
          eventBubblingEnabled: true,
          element: (
            <>
              <p className={styles.description}>
                {t.version.fileOptimize.dialog.description()}
              </p>
              <div className={styles.form}>
                <DragAndDropFields
                  accept={accept}
                  uploadedFileList={uploadedFileList}
                  setUploadedFileList={setUploadedFileList}
                  existingType={existingType}
                  setExistingType={setExistingType}
                  fileTypeConfigs={[
                    { type: "excel", limit: 1 },
                    { type: "json", limit: 1 },
                    { type: "zip", limit: 1 },
                  ]}
                  extElement={
                    uploadedFileList.length > 0 ? (
                      <>
                        <div className={styles.subtitle}>
                          {t.upload.dataName()}
                        </div>
                        {uploadedFileList.map(
                          ({ file, id, isDisableDelete }) => {
                            return (
                              <RenderUploadedRow
                                key={id}
                                file={file}
                                id={id}
                                isDisableDeleteButton={
                                  dialogPhase !== "confirm" || isDisableDelete
                                }
                                onDelete={() => {
                                  setUploadedFileList((state) =>
                                    state.filter((v) => v.id !== id)
                                  );
                                }}
                              />
                            );
                          }
                        )}
                      </>
                    ) : undefined
                  }
                />
                <Checkbox
                  label={t.version.fileOptimize.dialog.evaluateOnly()}
                  checked={isEvaluate}
                  indicator={{ style: { alignSelf: "center" } }}
                  onChange={(ev, data) => {
                    if (typeof data.checked === "string") return;
                    setIsEvaluate((_prev) => !!data.checked);
                  }}
                  disabled={
                    hasOptimizeWritePermission != hasEvaluateWritePermission
                  }
                />
                <Field
                  label={t.version.fileOptimize.dialog.versionTitle()}
                  required
                >
                  <Input
                    className={styles.input}
                    value={fileOptimizeForm.title}
                    onChange={(event, data) => {
                      handleChangeTextField(event, "title", data.value);
                    }}
                  />
                </Field>
                <Field label={t.version.fileOptimize.dialog.versionComment()}>
                  <Input
                    className={styles.input}
                    value={fileOptimizeForm.comment}
                    onChange={(event, data) => {
                      handleChangeTextField(event, "comment", data.value);
                    }}
                  />
                </Field>
                {displayedCategoriesDropdown && (
                  <Field label={t.version.fileOptimize.dialog.category()}>
                    <Dropdown
                      className={styles.dropdown}
                      onOptionSelect={onChangeCategory}
                      value={
                        displayedCategoriesDropdown.find(
                          (c) => c.value === fileOptimizeForm.category
                        )?.text ?? ""
                      }
                      selectedOptions={[
                        displayedCategoriesDropdown.find(
                          (c) => c.value === fileOptimizeForm.category
                        )?.text ?? "",
                      ]}
                    >
                      {displayedCategoriesDropdown.map((c) => (
                        <Option key={c.value} value={c.value}>
                          {c.text}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                )}
                {algorithmVersions ? (
                  <Field
                    label={t.version.fileOptimize.dialog.algorithmVersion()}
                    required
                  >
                    <Dropdown
                      className={styles.dropdown}
                      onOptionSelect={onChangeAlgorithmVersion}
                      value={
                        algorithmVersionOptions.find(
                          (v) => v.value === fileOptimizeForm.algorithm_version
                        )?.text ?? ""
                      }
                      selectedOptions={[
                        algorithmVersionOptions.find(
                          (v) => v.value === fileOptimizeForm.algorithm_version
                        )?.text ?? "",
                      ]}
                    >
                      {algorithmVersionOptions.map((v) => (
                        <Option key={v.value} value={v.value}>
                          {v.text}
                        </Option>
                      ))}
                    </Dropdown>
                    {algorithmVersionOptions.length === 0 && (
                      <div className={styles.errorMessage}>
                        {t.version.fileOptimize.dialog.noAlgorithmVersion()}
                      </div>
                    )}
                  </Field>
                ) : (
                  <div>
                    {t.version.fileOptimize.dialog.noAlgorithmVersionPermission()}
                  </div>
                )}
                {fileOptimizeForm.algorithm_version && (
                  <div className={styles.algorithmVersionId}>
                    <div>
                      {t.version.fileOptimize.dialog.algorithmVersionId()}
                    </div>
                    <div>{fileOptimizeForm.algorithm_version}</div>
                  </div>
                )}
              </div>
            </>
          ),
        },
      })}
    </>
  );
};
